import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { normalizeId, clientApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { MAX_FILE_SIZE, ALLOWED_IMAGE_TYPES } from '../../utils/config';
import {
  Container,
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  Alert,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Switch,
  CardActions,
  useTheme,
  alpha,
  InputAdornment,
  Rating,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  ListItemAvatar,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Assignment,
  Star,
  LocationOn,
  Notifications,
  FormatQuote,
  Visibility,
  Message,
  PhotoCamera,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import PageBackButton from '../../components/PageBackButton';

interface User {
  id: string;
  email: string;
  fullName: string;
  phone: string;
  user_type: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  avatar_url: string;
  rating: number;
  notification_settings: {
    new_offers: boolean;
    messages: boolean;
    status_updates: boolean;
  };
  service_requests: Array<{
    id: string;
    title: string;
    status: string;
    date: string;
    professional_name: string;
  }>;
  favorite_professionals: Array<{
    id: string;
    name: string;
    title: string;
    rating: number;
  }>;
  reviews: Array<{
    id: string;
    professional_id: string;
    professional_name: string;
    rating: number;
    comment: string;
    date: string;
  }>;
  total_requests: number;
  completed_requests: number;
  unread_messages: number;
}

const ClientProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { user } = useAuth() as AuthContextType;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<Partial<Client>>({});
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const [statusFilter, setStatusFilter] = useState('all');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  const clientId = id || '';

  // Проверка при първоначално зареждане дали потребителят е професионалист
  useEffect(() => {
    // Проверяваме дали потребителят гледа собствения си профил
    if (user && user.id.toString() === clientId) {
      // Ако имейлът е на професионалист и user_type също е professional, пренасочваме
      if (user.email === 'mitat1976@abv.bg' && user.user_type === 'professional') {
        navigate(`/profile/professional/${clientId}/dashboard`);
      }
    }
  }, [user, clientId, navigate]);

  const { data: client, isLoading, error } = useQuery<Client | null>({
    queryKey: ['client', id],
    queryFn: async () => {
      if (!id) {
        throw new Error('No client ID provided');
      }
      
      try {
        const response = await api.get(`/api/v1/clients/${id}`);
        
        if (!response.data.name && response.data.full_name) {
          response.data.name = response.data.full_name;
        }
        
        const enrichedData = {
          ...response.data,
          notification_settings: response.data.notification_settings || {
            new_offers: true,
            messages: true,
            status_updates: true
          },
          service_requests: response.data.service_requests || [],
          favorite_professionals: response.data.favorite_professionals || [],
          reviews: response.data.reviews || [],
          total_requests: response.data.total_requests || 0,
          completed_requests: response.data.completed_requests || 0,
          unread_messages: response.data.unread_messages || 0
        };
        
        return enrichedData;
      } catch (error: any) {
        const statusCode = error?.response?.status;
        if (statusCode === 404) {
          return null;
        }
        console.error('Error fetching client data:', error);
        throw error;
      }
    },
    enabled: !!id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<Client>) => {
      if (!clientId) {
        throw new Error('No client ID provided');
      }
      try {
        const response = await api.patch(`/api/v1/clients/${clientId}`, data);
        return response.data;
      } catch (error) {
        console.error('Update error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      setIsEditing(false);
      setValidationErrors({});
    },
    onError: (error: any) => {
      console.error('Error updating profile:', error);
      if (error.response?.data?.message) {
        setValidationErrors({ submit: error.response.data.message });
      } else {
        enqueueSnackbar(t('profile.save_error'), { variant: 'error' });
      }
    }
  });

  const handleEdit = () => {
    setEditedData(client || {});
    setIsEditing(true);
  };

  const validateFields = () => {
    const errors: Record<string, string> = {};
    
    if (!editedData.name?.trim()) {
      errors.name = t('validation.required');
    }
    
    if (!editedData.email?.trim()) {
      errors.email = t('validation.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedData.email)) {
      errors.email = t('validation.invalid_email');
    }
    
    if (editedData.phone && !/^\+?[\d\s-]{6,}$/.test(editedData.phone)) {
      errors.phone = t('validation.invalid_phone');
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateFields()) {
      return;
    }

    if (editedData) {
      try {
        const simplifiedData = {
          name: editedData.name?.trim() || '',
          email: editedData.email?.trim() || '',
          phone: editedData.phone?.trim() || '',
          location: editedData.location?.trim() || '',
          avatar_url: editedData.avatar_url || ''
        };
        
        await updateMutation.mutateAsync(simplifiedData);
      } catch (error) {
        console.error('Error saving profile:', error);
        enqueueSnackbar(t('profile.save_error'), { variant: 'error' });
      }
    }
  };

  const handleCancel = () => {
    setEditedData({});
    setIsEditing(false);
  };

  const handleChange = (field: keyof Client, value: any) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const isOwner = user?.id?.toString() === clientId?.toString() && user?.user_type === 'client';

  // Статуси и цветове
  const statusColors: Record<string, string> = {
    pending: theme.palette.warning.main,
    in_progress: theme.palette.info.main,
    completed: theme.palette.success.main,
    cancelled: theme.palette.error.main,
  };

  const StatusIcons: Record<string, React.ReactNode> = {
    pending: <Assignment />,
    in_progress: <Assignment />,
    completed: <Assignment />,
    cancelled: <Assignment />,
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      enqueueSnackbar('Моля, изберете валиден формат на изображението (JPEG, PNG или GIF)', { variant: 'error' });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar('Файлът е твърде голям. Максималният размер е 5MB', { variant: 'error' });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploadProgress(0);
    setIsUploading(true);

    api.post(`/api/v1/me/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        setUploadProgress(progress);
      }
    })
    .then(response => {
      setEditedData(prev => ({ ...prev, avatar_url: response.data.avatar_url }));
      queryClient.setQueryData(['client', id], (oldData: any) => ({
        ...oldData,
        avatar_url: response.data.avatar_url
      }));
      enqueueSnackbar('Снимката е качена успешно', { variant: 'success' });
    })
    .catch(error => {
      console.error('Грешка при качването на снимката:', error);
      enqueueSnackbar('Грешка при качването на снимката', { variant: 'error' });
    })
    .finally(() => {
      setIsUploading(false);
      setUploadProgress(0);
    });
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post(`/api/v1/me/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(progress);
        }
      });

      setEditedData(prev => ({ ...prev, avatar_url: response.data.avatar_url }));
      setShowPreview(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      enqueueSnackbar(t('profile.upload_success'), { variant: 'success' });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : t('profile.upload_failed'));
      enqueueSnackbar(t('profile.upload_failed'), { variant: 'error' });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancelUpload = () => {
    setShowPreview(false);
    setPreviewUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAvatarClick = () => {
    // Позволява промяна на аватар само, ако потребителят е в режим на редактиране
    if (isEditing) {
      fileInputRef.current?.click();
    }
  };

  // Показване на зареждащ индикатор
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <PageBackButton />
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Зареждане на профила...</Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Показване на грешка
  if (error) {
    const statusCode = (error as any)?.response?.status;
    if (statusCode === 404) {
      return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <PageBackButton />
          <Alert severity="info">
            {t('common.no_results')}
          </Alert>
          <Typography>
            Не е намерен клиент с този идентификатор.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Към началната страница
          </Button>
        </Container>
      );
    }

    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <PageBackButton />
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('common.error')}
        </Alert>
        <Typography>
          Възникна грешка при зареждане на профила. Сървърът може да не работи или API ендпойнтът не съществува.
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
          Грешка: {(error as Error).message}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Към началната страница
        </Button>
      </Container>
    );
  }

  if (!client) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <PageBackButton />
        <Alert severity="info">
          {t('common.no_results')}
        </Alert>
        <Typography>
          Не е намерен клиент с този идентификатор.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Към началната страница
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageBackButton />
      <Paper elevation={0} sx={{ mb: 4, borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                <IconButton
                  onClick={handleAvatarClick}
                  sx={{
                    position: 'relative',
                    cursor: isEditing ? 'pointer' : 'default',
                    '&:hover': isEditing ? {
                      '& .MuiAvatar-root': {
                        opacity: 0.7,
                      },
                      '& .MuiSvgIcon-root': {
                        opacity: 1,
                      },
                    } : {},
                  }}
                >
                  <Avatar
                    src={isEditing ? editedData.avatar_url : client.avatar_url}
                    alt={client.name}
                    sx={{ width: 150, height: 150, mb: 2, transition: 'opacity 0.3s' }}
                  />
                  {isEditing && (
                    <PhotoCamera
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        opacity: 0,
                        transition: 'opacity 0.3s',
                        color: 'white',
                        fontSize: 40,
                      }}
                    />
                  )}
                </IconButton>
                {isEditing && (
                  <TextField
                    label={t('profile.avatar_url')}
                    value={editedData.avatar_url || ''}
                    onChange={(e) => setEditedData({ ...editedData, avatar_url: e.target.value })}
                    fullWidth
                    margin="normal"
                    size="small"
                  />
                )}
                {/* Edit Profile Button - now shows only here when not editing and user is owner */}
                {!isEditing && isOwner && (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    color="primary"
                    sx={{ 
                      mt: 2, 
                      width: '100%',
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      }
                    }}
                  >
                    {t('profile.edit')}
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <Box sx={{ pl: { md: 2 } }}>
                {isEditing ? (
                  <TextField
                    label={t('common.name')}
                    value={editedData.name || ''}
                    onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
                    fullWidth
                    margin="normal"
                    size="medium"
                    error={!!validationErrors.name}
                    helperText={validationErrors.name}
                    InputProps={{
                      style: { fontSize: '1.5rem', fontWeight: 'bold' },
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" component="h1" fontWeight="bold">
                      {client.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Rating value={client.rating} readOnly precision={0.5} />
                      <Typography variant="body2" component="div" sx={{ ml: 1 }}>
                        {(client.rating ?? 0).toFixed(1)}
                      </Typography>
                    </Box>
                    {isOwner && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={handleEdit}
                        size="large"
                        sx={{ 
                          textTransform: 'none', 
                          display: isEditing ? 'none' : 'flex',
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                          '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                          },
                          minWidth: '120px',
                          height: '40px',
                          borderRadius: '20px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          '&:active': {
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                          }
                        }}
                      >
                        {t('profile.edit')}
                      </Button>
                    )}
                  </Box>
                )}

                <TextField
                  label={t('common.email')}
                  value={isEditing ? editedData.email : client.email}
                  onChange={(e) => isEditing && setEditedData({ ...editedData, email: e.target.value })}
                  fullWidth
                  margin="normal"
                  error={!!validationErrors.email}
                  helperText={validationErrors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                    readOnly: !isEditing,
                  }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  label={t('common.phone')}
                  value={isEditing ? editedData.phone : client.phone}
                  onChange={(e) => isEditing && setEditedData({ ...editedData, phone: e.target.value })}
                  fullWidth
                  margin="normal"
                  error={!!validationErrors.phone}
                  helperText={validationErrors.phone}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone />
                      </InputAdornment>
                    ),
                    readOnly: !isEditing,
                  }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  label={t('common.location')}
                  value={isEditing ? editedData.location : client.location}
                  onChange={(e) => isEditing && setEditedData({ ...editedData, location: e.target.value })}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn />
                      </InputAdornment>
                    ),
                    readOnly: !isEditing,
                  }}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  {isEditing && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                      >
                        {t('common.save')}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                      >
                        {t('common.cancel')}
                      </Button>
                    </Box>
                  )}
                  {!isEditing && !isOwner && (
                    <Button
                      variant="contained"
                      startIcon={<Message />}
                      sx={{ 
                        alignSelf: 'flex-start', 
                        mt: 2,
                        bgcolor: alpha(theme.palette.common.white, 0.2),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.common.white, 0.3),
                        }
                      }}
                    >
                      {t('profile.contact')}
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Съдържание с табове */}
      <Paper elevation={0} sx={{ 
        borderRadius: 2,
        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
      }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="fullWidth"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTabs-indicator': {
              height: 3,
            },
          }}
        >
          <Tab 
            icon={<Assignment />} 
            label={t('profile.service_requests')} 
            iconPosition="start"
            sx={{ 
              fontWeight: 'medium',
              textTransform: 'none',
              fontSize: '1rem',
            }}
          />
          <Tab 
            icon={<Star />} 
            label={t('profile.favorite_professionals')} 
            iconPosition="start"
            sx={{ 
              fontWeight: 'medium',
              textTransform: 'none',
              fontSize: '1rem',
            }}
          />
          <Tab 
            icon={<FormatQuote />} 
            label={t('profile.reviews')} 
            iconPosition="start"
            sx={{ 
              fontWeight: 'medium',
              textTransform: 'none',
              fontSize: '1rem',
            }}
          />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {/* Заявки за услуги */}
          {activeTab === 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">{t('common.filters')}</Typography>
                <Box sx={{ width: '100%', mt: 1 }}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel id="status-filter-label">{t('common.status')}</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as string)}
                      label={t('common.status')}
                    >
                      <MenuItem value="all">{t('common.all')}</MenuItem>
                      <MenuItem value="pending">{t('common.pending')}</MenuItem>
                      <MenuItem value="in_progress">{t('common.in_progress')}</MenuItem>
                      <MenuItem value="completed">{t('common.completed')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <List sx={{ maxHeight: 300, overflow: 'auto', p: 0 }}>
                {client.service_requests.length > 0 ? (
                  client.service_requests.map((request) => (
                    <ListItem
                      key={request.id}
                      divider
                      secondaryAction={
                        <IconButton edge="end" aria-label="view">
                          <Visibility />
                        </IconButton>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: statusColors[request.status] }}>
                          {StatusIcons[request.status]}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            {request.title}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {request.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip
                                size="small"
                                label={String(t(`common.${request.status}`))}
                                sx={{
                                  bgcolor: alpha(statusColors[request.status], 0.1),
                                  color: statusColors[request.status],
                                  fontWeight: 'bold',
                                }}
                              />
                              <Chip
                                size="small"
                                label={request.date}
                                sx={{ bgcolor: 'grey.100' }}
                              />
                            </Box>
                          </>
                        }
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ textAlign: 'center', py: 2 }}>
                          {t('common.no_requests_found')}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          {/* Любими специалисти */}
          {activeTab === 1 && (
            <Box>
              {client.favorite_professionals.length > 0 ? (
                <Grid container spacing={3}>
                  {client.favorite_professionals.map((professional) => (
                    <Grid item xs={12} sm={6} md={4} key={professional.id}>
                      <Card sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                        }
                      }}>
                        <Box sx={{ 
                          height: 100, 
                          bgcolor: theme.palette.primary.main, 
                          position: 'relative',
                          display: 'flex',
                          justifyContent: 'center',
                          pt: 5
                        }}>
                          <Avatar 
                            src="" 
                            sx={{ 
                              width: 80, 
                              height: 80, 
                              border: '3px solid white',
                              position: 'absolute',
                              top: '100%',
                              transform: 'translateY(-50%)',
                              boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                            }}
                          >
                            <Person />
                          </Avatar>
                        </Box>
                        <CardContent sx={{ pt: 5, pb: 1, textAlign: 'center' }}>
                          <Typography variant="h6" component="div" gutterBottom>{professional.name}</Typography>
                          <Typography variant="body2" component="div" color="text.secondary" gutterBottom>
                            {professional.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                            <Star sx={{ color: theme.palette.warning.main, mr: 0.5 }} />
                            <Typography component="div">{(professional.rating ?? 0).toFixed(1)}</Typography>
                          </Box>
                        </CardContent>
                        <CardActions sx={{ p: 2, pt: 0, mt: 'auto', justifyContent: 'center' }}>
                          <Button 
                            variant="outlined" 
                            size="small"
                            onClick={() => navigate(`/profile/professional/${normalizeId(professional.id)}`)}
                            startIcon={<Visibility />}
                          >
                            {t('common.view_profile')}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t('profile.no_favorite_professionals')}
                </Alert>
              )}
            </Box>
          )}

          {/* Отзиви */}
          {activeTab === 2 && (
            <Box>
              {client.reviews.length > 0 ? (
                <Grid container spacing={3}>
                  {client.reviews.map((review) => (
                    <Grid item xs={12} key={review.id}>
                      <Paper sx={{ 
                        p: 3, 
                        borderRadius: 2, 
                        boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          fontSize: '6rem', 
                          opacity: 0.05, 
                          color: theme.palette.primary.main,
                          lineHeight: 1 
                        }}>
                          "
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" component="div" sx={{ mr: 1 }}>{review.professional_name}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                            <Rating value={review.rating} readOnly precision={0.5} />
                            <Typography variant="body2" component="div" sx={{ ml: 1 }}>
                              {(review.rating ?? 0).toFixed(1)}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body1" component="div" sx={{ position: 'relative', zIndex: 1, pl: 1 }}>
                          "{review.comment}"
                        </Typography>
                        <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'right' }}>
                          {review.date}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t('profile.no_reviews')}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Настройки на известията - само за собственика на профила */}
      {isOwner && (
        <Paper elevation={0} sx={{ 
          mt: 4, 
          p: 3, 
          borderRadius: 2,
          boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
        }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Notifications sx={{ mr: 1 }} />
            {t('profile.notification_settings')}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <List sx={{ width: '100%' }}>
            <ListItem>
              <ListItemText 
                primary={t('profile.new_offers')} 
                secondary={t('profile.new_offers_description')} 
              />
              <Switch 
                checked={isEditing 
                  ? (editedData.notification_settings?.new_offers ?? client?.notification_settings.new_offers)
                  : client?.notification_settings.new_offers}
                onChange={async (e) => {
                  if (isEditing) {
                    const updatedSettings = {
                      ...editedData.notification_settings || client?.notification_settings || {},
                      new_offers: e.target.checked
                    };
                    handleChange('notification_settings', updatedSettings);
                  } else {
                    // Директно обновяване без режим на редактиране
                    try {
                      await clientApi.updateClientProfile(clientId, {
                        notification_settings: {
                          ...client?.notification_settings,
                          new_offers: e.target.checked
                        }
                      });
                    } catch (error) {
                      console.error('Грешка при обновяване на настройките за известия:', error);
                      // Връщаме опцията в първоначалното състояние
                      e.target.checked = client?.notification_settings.new_offers || false;
                    }
                  }
                }}
                color="primary"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary={t('profile.messages')} 
                secondary={t('profile.messages_description')} 
              />
              <Switch 
                checked={isEditing 
                  ? (editedData.notification_settings?.messages ?? client?.notification_settings.messages)
                  : client?.notification_settings.messages}
                onChange={async (e) => {
                  if (isEditing) {
                    const updatedSettings = {
                      ...editedData.notification_settings || client?.notification_settings || {},
                      messages: e.target.checked
                    };
                    handleChange('notification_settings', updatedSettings);
                  } else {
                    // Директно обновяване без режим на редактиране
                    try {
                      await clientApi.updateClientProfile(clientId, {
                        notification_settings: {
                          ...client?.notification_settings,
                          messages: e.target.checked
                        }
                      });
                    } catch (error) {
                      console.error('Грешка при обновяване на настройките за известия:', error);
                      // Връщаме опцията в първоначалното състояние
                      e.target.checked = client?.notification_settings.messages || false;
                    }
                  }
                }}
                color="primary"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary={t('profile.status_updates')} 
                secondary={t('profile.status_updates_description')} 
              />
              <Switch 
                checked={isEditing 
                  ? (editedData.notification_settings?.status_updates ?? client?.notification_settings.status_updates)
                  : client?.notification_settings.status_updates}
                onChange={async (e) => {
                  if (isEditing) {
                    const updatedSettings = {
                      ...editedData.notification_settings || client?.notification_settings || {},
                      status_updates: e.target.checked
                    };
                    handleChange('notification_settings', updatedSettings);
                  } else {
                    // Директно обновяване без режим на редактиране
                    try {
                      await clientApi.updateClientProfile(clientId, {
                        notification_settings: {
                          ...client?.notification_settings,
                          status_updates: e.target.checked
                        }
                      });
                    } catch (error) {
                      console.error('Грешка при обновяване на настройките за известия:', error);
                      // Връщаме опцията в първоначалното състояние
                      e.target.checked = client?.notification_settings.status_updates || false;
                    }
                  }
                }}
                color="primary"
              />
            </ListItem>
          </List>
        </Paper>
      )}
      <Dialog open={showPreview} onClose={handleCancelUpload} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={previewUrl || ''}
              alt="Preview"
              sx={{ width: 200, height: 200, mb: 2 }}
            />
            {uploadError && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {uploadError}
              </Alert>
            )}
            {isUploading && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                  {t('profile.uploading')} {Math.round(uploadProgress)}%
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('profile.max_file_size', { size: '5MB' })}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUpload} color="error">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={() => selectedFile && handleUpload()} 
            variant="contained" 
            color="primary"
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={20} /> : null}
          >
            {isUploading ? t('common.uploading') : t('common.upload')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientProfile; 